import React, { useEffect, useState } from 'react';
import { Box, Typography, CircularProgress, Alert } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';

const BillingSuccess = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [verificationResult, setVerificationResult] = useState(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const amount = searchParams.get('amount') || sessionStorage.getItem('mockPaymentAmount');

  // Function to directly update user balance for mock payments
  const updateMockBalance = async (amount) => {
    try {
      // Get the token
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication required');
      }

      // First, fetch the current balance
      const balanceResponse = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/payment/get-balance`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!balanceResponse.ok) {
        throw new Error('Failed to fetch current balance');
      }
      
      const balanceData = await balanceResponse.json();
      const currentBalance = parseFloat(balanceData.balance || 0);
      const parsedAmount = parseFloat(amount);
      const newBalance = currentBalance + parsedAmount;

      console.log('Current Balance:', currentBalance);
      console.log('Amount to add:', parsedAmount);
      console.log('New Balance:', newBalance);
      
      // Now, directly call the verify-session endpoint to update the balance
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/payment/verify-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          session_id: sessionId,
          mock_payment: true,
          amount: parsedAmount
        })
      });
      
      const data = await response.json();
      console.log('Mock payment response:', data);
      
      if (!response.ok) {
        throw new Error(data.error || 'Failed to update balance');
      }
      
      // Set the verification result for display
      setVerificationResult({
        success: true,
        message: 'Mock payment processed successfully',
        new_balance: newBalance,
        formatted_balance: `$${newBalance.toFixed(2)}`
      });
      
      return true;
    } catch (err) {
      console.error('Error updating mock balance:', err);
      throw err;
    }
  };

  // Function to verify the payment and update balance
  const verifyPayment = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      if (!token) {
        throw new Error('Authentication required');
      }
      
      // Check if this is a mock payment session
      if (sessionId && sessionId.startsWith('mock_sess_')) {
        console.log('Processing mock payment:', sessionId, 'Amount:', amount);
        
        // For mock payments, directly update the balance
        if (amount) {
          await updateMockBalance(amount);
        } else {
          throw new Error('No amount specified for mock payment');
        }
        
        // Short delay before redirecting
        setTimeout(() => {
          setLoading(false);
          setTimeout(() => {
            navigate('/billing');
          }, 3000);
        }, 1500);
        
        return;
      }
      
      // Call the verify-session endpoint to check payment status and update balance
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/payment/verify-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ session_id: sessionId })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Failed to verify payment');
      }
      
      setVerificationResult(data);
      
      // Set a timer to redirect to billing page
      setTimeout(() => {
        navigate('/billing');
      }, 3000);
      
    } catch (err) {
      console.error('Error verifying payment:', err);
      setError(err.message || 'Failed to verify payment');
      
      // Even if verification fails, redirect to billing after a slightly longer delay
      setTimeout(() => {
        navigate('/billing');
      }, 5000);
    } finally {
      if (!sessionId?.startsWith('mock_sess_')) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    // Clean up session storage
    const cleanupStorage = () => {
      sessionStorage.removeItem('mockPaymentAmount');
    };

    // If we have a session ID, verify the payment
    if (sessionId) {
      verifyPayment();
    } else {
      setError('No session ID found');
      setLoading(false);
      
      // Redirect if no session ID
      setTimeout(() => {
        navigate('/billing');
      }, 3000);
    }

    return cleanupStorage;
  }, [sessionId, navigate]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '60vh',
      }}
    >
      {!error ? (
        <CheckCircleIcon color="success" sx={{ fontSize: 60, mb: 2 }} />
      ) : (
        <ErrorIcon color="error" sx={{ fontSize: 60, mb: 2 }} />
      )}
      
      <Typography variant="h4" gutterBottom>
        {!error ? 'Payment Successful!' : 'Payment Verification Issue'}
      </Typography>
      
      {loading ? (
        <>
          <Typography variant="body1" align="center" color="text.secondary" sx={{ maxWidth: 500, mb: 2 }}>
            Verifying your payment and updating your balance...
          </Typography>
          <CircularProgress size={24} sx={{ mt: 2 }} />
        </>
      ) : error ? (
        <>
          <Alert severity="error" sx={{ maxWidth: 500, mb: 2 }}>
            {error}
          </Alert>
          <Typography variant="body2" color="text.secondary">
            Redirecting to billing page...
          </Typography>
        </>
      ) : (
        <>
          <Typography variant="body1" align="center" color="text.secondary" sx={{ maxWidth: 500, mb: 2 }}>
            Your payment has been verified and your balance has been updated to {verificationResult?.formatted_balance || 'your new balance'}.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Redirecting to billing page...
          </Typography>
        </>
      )}
    </Box>
  );
};

export default BillingSuccess; 